import { Component } from "react";
import { connect } from "react-redux";
import { type InjectedFormProps, SubmissionError, reduxForm } from "redux-form";

import { Dialog } from "../core/overlays/Dialogs.js";
import CheckboxField, { CheckboxReduxFormField } from "../core/redux-form/CheckboxField.js";
import ReactSelectField, { ReactSelectReduxFormField } from "../core/redux-form/ReactSelectField.js";
import { type ISelectOption, simpleDropdownMultiSelectFieldValidation } from "@/app-utils/form.ts";
import { type InputAddProjectMemberInput, type Project, type User } from "@/graphql.ts";
import { getProjectLabel } from "@/redux/modules/config.ts";
import { addProjectMembers } from "@/redux/modules/project.ts";
import { PUBLIC_USER } from "@/settings/settings.js";

interface IAddMemberPopoverOwnProps {
  onHide: () => void;
  project: Project;
}

interface IAddMemberPopoverDispatchProps {
  addProjectMembers: (input: InputAddProjectMemberInput) => Promise<boolean>;
}

export interface IAddMemberPopoverProps
  extends IAddMemberPopoverDispatchProps,
    IAddMemberPopoverOwnProps,
    InjectedFormProps<any, any> {}

function mapDispatchToProps(dispatch): IAddMemberPopoverDispatchProps {
  return {
    addProjectMembers: (input: InputAddProjectMemberInput) => dispatch(addProjectMembers(input)),
  };
}

class AddMemberPopover extends Component<IAddMemberPopoverProps> {
  public static PROJECT_DIALOG_FIELD_MEMBERS = "selectUser";
  public static PROJECT_DIALOG_FIELD_SENDEMAILS = "sendEmails";

  isNotMember(sesId: number) {
    return !this.props.project.projectMembers?.find((m) => m.user.id === sesId);
  }

  buildUserList = (): Array<{ value; label }> => {
    // Rules:
    // You can either select 'Public' or any number of the other users.
    //  If none is selected -> [ public , user1, user2, ... ]
    //  If public is selected -> [ ]
    //  If user1 is selected -> [ user2, ... ]

    const users: User[] = !this.props.project
      ? []
      : this.props.project.userGroup.members.filter((u) => {
          return u.username !== PUBLIC_USER.username && this.isNotMember(u.id);
        });

    return users.map((u) => {
      return {
        value: u.id,
        label: `${u.username}${u.firstName && u.lastName ? " (" + u.firstName + " " + u.lastName + ")" : ""}`,
      };
    });
  };

  handleSubmitData = (values: FormData) => {
    const addProjectUsersInput: InputAddProjectMemberInput = {
      userIds: values[AddMemberPopover.PROJECT_DIALOG_FIELD_MEMBERS].map((u: ISelectOption) => {
        return u.value;
      }),
      projectId: this.props.project.id,
      emailNotification: !!values[AddMemberPopover.PROJECT_DIALOG_FIELD_SENDEMAILS],
    };

    return this.props.addProjectMembers(addProjectUsersInput).then(
      (fulfilled) => {
        this.props.reset();
        this.props.onHide();
        return fulfilled;
      },
      (rejected) => {
        throw new SubmissionError({ _error: rejected.message });
      },
    );
  };

  render() {
    return (
      <Dialog
        headerProps={{ title: `Add New ${getProjectLabel(true, false)} Members` }}
        closeFn={() => this.props.onHide()}
        mainActionButtonProps={{
          children: "Save",
          onPress: this.props.handleSubmit(this.handleSubmitData),
          isLoading: this.props.submitting,
        }}
        footerMessage={this.props.error ? { message: this.props.error } : undefined}
      >
        <ReactSelectReduxFormField
          name={AddMemberPopover.PROJECT_DIALOG_FIELD_MEMBERS}
          label={`Users to be added as new ${getProjectLabel(false, false)} members`}
          placeholder={`Select users... `}
          components={{ IndicatorsContainer: () => null }}
          isClearable={false}
          openMenuOnFocus={true}
          options={this.buildUserList()}
          component={ReactSelectField}
          isMulti={true}
          closeMenuOnSelect={false}
          validate={simpleDropdownMultiSelectFieldValidation}
        />
        <CheckboxReduxFormField
          name={AddMemberPopover.PROJECT_DIALOG_FIELD_SENDEMAILS}
          label={`Notify new ${getProjectLabel(false, false)} members via email`}
          component={CheckboxField}
        />
      </Dialog>
    );
  }
}

export default connect<{}, IAddMemberPopoverDispatchProps, IAddMemberPopoverOwnProps>(
  undefined,
  mapDispatchToProps,
)(
  reduxForm({
    form: "addMemberPopover",
    initialValues: {},
    // validate: CreateProjectDialog.validate, // we use field level validation
    // asyncValidate:  AddMemberPopover.asyncValidate,
    // asyncBlurFields: [AddMemberPopover.PROJECT_DIALOG_FIELD_NAME] // so that only blurring this field triggers the async validation
  })(AddMemberPopover) as any,
);
